/**
 * AccountSection — Settings → Account
 *
 * The identity half of Settings: who you are, how you sign in, and how you
 * leave. Everything here was read-only until now, with copy telling people to
 * email support to change their own name.
 *
 * Three things ask for the current password before they'll proceed — changing
 * the password, changing the email, and deleting the account. That's the
 * "danger zone" convention, and the reason is a walked-away laptop: each of
 * those actions either locks the real owner out or destroys their data.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { authApi, accountApi } from '../../services/api'
import { JAI_COLORS, JAI_FONTS } from '../JaiBrand'
import { GhostButton, SolidButton } from '../ModalPortal'
import {
  Row, EditableRow, SectionNote, Notice, FieldError, PasswordField, formatDate,
} from './SettingsFields'
import { IS_NATIVE_APP } from '../../services/runtime'

/** A titled block inside the tab, with a rule above it. */
function Block({ title, children, tone = 'normal' }) {
  return (
    <div style={{ marginTop: 26, paddingTop: 20, borderTop: `1px solid ${JAI_COLORS.hair}` }}>
      <div style={{
        fontSize: 14, fontWeight: 700, fontFamily: JAI_FONTS.tight,
        color: tone === 'danger' ? JAI_COLORS.error : JAI_COLORS.navy,
      }}>
        {title}
      </div>
      {children}
    </div>
  )
}

export default function AccountSection({ details, onUpdated, onTokenReplaced, onDeleted }) {
  const fullName = [details.firstName, details.lastName].filter(Boolean).join(' ')

  // Which expandable panel is open: '' | 'password' | 'email' | 'delete'
  const [panel, setPanel] = useState('')
  const togglePanel = (name) => setPanel(p => (p === name ? '' : name))

  // ── Change password ───────────────────────────────────────────────────────
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pwBusy, setPwBusy] = useState(false)
  const [pwError, setPwError] = useState('')
  const [pwDone, setPwDone] = useState(false)

  const submitPassword = async () => {
    setPwError('')
    if (newPassword !== confirmPassword) {
      setPwError('Those two new passwords don\'t match.')
      return
    }
    setPwBusy(true)
    try {
      const { data } = await accountApi.changePassword(currentPassword, newPassword)
      // The server invalidated every token issued before now — including the
      // one this browser is holding — and handed back a replacement.
      onTokenReplaced(data.token)
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('')
      setPwDone(true)
      setPanel('')
      setTimeout(() => setPwDone(false), 6000)
    } catch (err) {
      setPwError(err?.response?.data?.error || 'Could not change your password. Please try again.')
    } finally {
      setPwBusy(false)
    }
  }

  // ── Change email ──────────────────────────────────────────────────────────
  const [newEmail, setNewEmail] = useState('')
  const [emailPassword, setEmailPassword] = useState('')
  const [emailBusy, setEmailBusy] = useState(false)
  const [emailError, setEmailError] = useState('')
  const [emailSentTo, setEmailSentTo] = useState('')

  const submitEmail = async () => {
    setEmailError('')
    setEmailBusy(true)
    try {
      const { data } = await accountApi.requestEmailChange(newEmail, emailPassword)
      setEmailSentTo(data.pendingEmail)
      setNewEmail(''); setEmailPassword('')
      setPanel('')
      onUpdated({ pendingEmail: data.pendingEmail })
    } catch (err) {
      setEmailError(err?.response?.data?.error || 'Could not start the email change. Please try again.')
    } finally {
      setEmailBusy(false)
    }
  }

  const cancelPendingEmail = async () => {
    try {
      await accountApi.cancelEmailChange()
      setEmailSentTo('')
      onUpdated({ pendingEmail: null })
    } catch {
      setEmailError('Could not cancel the pending change. Please try again.')
    }
  }

  // ── Sign out everywhere ───────────────────────────────────────────────────
  const [sessionsBusy, setSessionsBusy] = useState(false)
  const [sessionsDone, setSessionsDone] = useState(false)
  const [sessionsError, setSessionsError] = useState('')

  // ── Sign out of this device — native only (see the button in the JSX) ────
  const { logout } = useAuth()
  const navigate = useNavigate()
  const [signingOut, setSigningOut] = useState(false)

  const signOutHere = async () => {
    setSigningOut(true)
    try {
      await logout()
      // Explicit, the same way AccountMenu.handleLogout does it on the web:
      // ProtectedRoute does send a session-less native visitor to /welcome on
      // its own, but the redirect shouldn't depend on that staying true.
      navigate('/welcome', { replace: true })
    } finally {
      setSigningOut(false)
    }
  }

  const signOutEverywhere = async () => {
    setSessionsBusy(true)
    setSessionsError('')
    try {
      const { data } = await accountApi.logoutEverywhere()
      onTokenReplaced(data.token)
      setSessionsDone(true)
      setTimeout(() => setSessionsDone(false), 6000)
    } catch (err) {
      setSessionsError(err?.response?.data?.error || 'Could not sign out your other devices. Please try again.')
    } finally {
      setSessionsBusy(false)
    }
  }

  // ── Delete account ────────────────────────────────────────────────────────
  const [deletePassword, setDeletePassword] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  const submitDelete = async () => {
    setDeleteError('')
    setDeleteBusy(true)
    try {
      await accountApi.deleteAccount(deletePassword)
      onDeleted()
    } catch (err) {
      setDeleteError(err?.response?.data?.error || 'Could not delete your account. Nothing has been changed.')
      setDeleteBusy(false)
    }
  }

  const pendingEmail = emailSentTo || details.pendingEmail

  return (
    <div>
      {/* ── Who you are ─────────────────────────────────────────────────── */}
      <EditableRow
        label="First name"
        value={details.firstName || ''}
        maxLength={50}
        onSave={async (v) => {
          const { data } = await accountApi.updateProfile({ firstName: v })
          onUpdated(data.user)
        }}
      />
      <EditableRow
        label="Last name"
        value={details.lastName || ''}
        maxLength={50}
        onSave={async (v) => {
          const { data } = await accountApi.updateProfile({ lastName: v })
          onUpdated(data.user)
        }}
      />
      <EditableRow
        label="Username"
        value={details.username || ''}
        display={details.username ? `@${details.username}` : '—'}
        prefix="@"
        maxLength={20}
        helper="3–20 characters. Letters, numbers and underscores only."
        checkAvailability={async (name) => {
          const { data } = await authApi.checkUsername(name)
          return data.available
        }}
        onSave={async (v) => {
          const { data } = await accountApi.updateProfile({ username: v })
          onUpdated(data.user)
        }}
      />

      <Row
        label="Email"
        value={
          <span>
            {details.email}
            {details.emailVerified && (
              <span style={{ marginLeft: 9, fontSize: 12, fontWeight: 600, color: JAI_COLORS.success }}>
                Verified
              </span>
            )}
          </span>
        }
      />
      <Row label="Member since" value={formatDate(details.createdAt, details.timezone)} />

      {pendingEmail && (
        <Notice tone="warn" title="Email change waiting on confirmation">
          We've sent a confirmation link to <strong>{pendingEmail}</strong>. Your
          email stays as <strong>{details.email}</strong> — and that's what you
          sign in with — until you click that link.
          <div style={{ marginTop: 10 }}>
            <GhostButton onClick={cancelPendingEmail}>Cancel this change</GhostButton>
          </div>
        </Notice>
      )}

      {/* ── Sign-in ─────────────────────────────────────────────────────── */}
      <Block title="Signing in">
        {pwDone && (
          <Notice tone="good">
            Password changed. You've been signed out everywhere else — this
            device stays signed in.
          </Notice>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 14 }}>
          <GhostButton onClick={() => togglePanel('password')}>
            {panel === 'password' ? 'Never mind' : 'Change password'}
          </GhostButton>
          <GhostButton onClick={() => togglePanel('email')}>
            {panel === 'email' ? 'Never mind' : 'Change email'}
          </GhostButton>
        </div>

        {panel === 'password' && (
          <Notice tone="neutral" title="Change your password">
            Changing it signs you out on every other device — which is exactly
            what you want if you think someone else has it.
            <PasswordField label="Current password" value={currentPassword} onChange={setCurrentPassword} />
            <PasswordField label="New password" value={newPassword} onChange={setNewPassword} autoComplete="new-password" />
            <PasswordField label="New password again" value={confirmPassword} onChange={setConfirmPassword} autoComplete="new-password" onEnter={submitPassword} />
            <div style={{ marginTop: 8, fontSize: 12.5, color: JAI_COLORS.mute }}>
              At least 8 characters, with one capital letter and one number.
            </div>
            <FieldError>{pwError}</FieldError>
            <div style={{ marginTop: 14 }}>
              <SolidButton onClick={submitPassword} loading={pwBusy} disabled={!currentPassword || !newPassword || !confirmPassword}>
                {pwBusy ? 'Changing…' : 'Change password'}
              </SolidButton>
            </div>
          </Notice>
        )}

        {panel === 'email' && (
          <Notice tone="neutral" title="Change your email">
            We'll send a confirmation link to the new address.{' '}
            <strong>Nothing changes until you click it</strong>, so a typo can't
            lock you out of your own account.
            <div style={{ marginTop: 12 }}>
              <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: JAI_COLORS.mute, marginBottom: 6 }}>
                New email address
              </label>
              <input
                type="email"
                value={newEmail}
                autoComplete="email"
                onChange={e => setNewEmail(e.target.value)}
                style={{
                  width: '100%', padding: '9px 11px', borderRadius: 9,
                  border: `1px solid ${JAI_COLORS.hair}`, fontFamily: JAI_FONTS.tight,
                  fontSize: 14.5, color: JAI_COLORS.navy, background: 'white',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>
            <PasswordField label="Your password" value={emailPassword} onChange={setEmailPassword} onEnter={submitEmail} />
            <FieldError>{emailError}</FieldError>
            <div style={{ marginTop: 14 }}>
              <SolidButton onClick={submitEmail} loading={emailBusy} disabled={!newEmail || !emailPassword}>
                {emailBusy ? 'Sending…' : 'Send confirmation link'}
              </SolidButton>
            </div>
          </Notice>
        )}

        {sessionsDone && (
          <Notice tone="good">
            Signed out everywhere else. Any other browser or device will need to
            log in again.
          </Notice>
        )}

        {/* Native only: on the web, signing out lives in the sidebar's account
            menu (AccountMenu.jsx). The native tab bar replaced that sidebar,
            which left the app with no way to sign out at all — the only
            button here was "Sign out on all other devices", which keeps
            *this* device signed in. */}
        {IS_NATIVE_APP && (
          <div style={{ marginTop: 16 }}>
            <SolidButton onClick={signOutHere} loading={signingOut}>
              {signingOut ? 'Signing out…' : 'Sign out'}
            </SolidButton>
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <GhostButton onClick={signOutEverywhere} disabled={sessionsBusy}>
            {sessionsBusy ? 'Signing out…' : 'Sign out on all other devices'}
          </GhostButton>
          <FieldError>{sessionsError}</FieldError>
        </div>

        <SectionNote>
          Use this if you've signed in somewhere you don't control — a shared
          computer, or a phone you no longer have. You stay signed in here.
        </SectionNote>
      </Block>

      {/* ── Danger zone ─────────────────────────────────────────────────── */}
      <Block title="Delete account" tone="danger">
        <SectionNote>
          Permanently deletes your JAI account and everything in it. This cannot
          be undone.
        </SectionNote>

        <div style={{ marginTop: 14 }}>
          <GhostButton onClick={() => togglePanel('delete')}>
            {panel === 'delete' ? 'Never mind' : 'Delete my account'}
          </GhostButton>
        </div>

        {panel === 'delete' && (
          <Notice tone="danger" title="This is permanent">
            <ul style={{ margin: '0 0 4px 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 9 }}>
              {[
                <>Your account, your connected leagues and your league data are <strong>deleted for good</strong>. There's no undo and no recovery.</>,
                IS_NATIVE_APP
                  ? <>Any subscription is <strong>cancelled immediately</strong>. If you've paid for time you haven't used yet, you give that up.</>
                  : <>Any subscription is <strong>cancelled immediately</strong>. If you've paid for time you haven't used yet, you give that up — cancel from the Subscription tab instead if you'd rather use it out first.</>,
                <>Notes saved in this browser stay on this device. Export your data first if you want a copy.</>,
                <>Payment records stay with Stripe for as long as tax law requires. Everything else goes.</>,
              ].map((fact, i) => (
                <li key={i} style={{ display: 'flex', gap: 9, fontSize: 13, lineHeight: 1.6, color: JAI_COLORS.navy }}>
                  <span aria-hidden="true" style={{ color: JAI_COLORS.error, fontWeight: 700, flexShrink: 0 }}>·</span>
                  <span>{fact}</span>
                </li>
              ))}
            </ul>

            <PasswordField label="Your password" value={deletePassword} onChange={setDeletePassword} />

            <div style={{ marginTop: 12 }}>
              <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: JAI_COLORS.mute, marginBottom: 6 }}>
                Type DELETE to confirm
              </label>
              <input
                value={deleteConfirm}
                onChange={e => setDeleteConfirm(e.target.value)}
                placeholder="DELETE"
                style={{
                  width: '100%', padding: '9px 11px', borderRadius: 9,
                  border: `1px solid ${JAI_COLORS.hair}`, fontFamily: JAI_FONTS.tight,
                  fontSize: 14.5, color: JAI_COLORS.navy, background: 'white',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>

            <FieldError>{deleteError}</FieldError>

            <div style={{ marginTop: 14 }}>
              <SolidButton
                tone="danger"
                onClick={submitDelete}
                loading={deleteBusy}
                disabled={!deletePassword || deleteConfirm !== 'DELETE'}
              >
                {deleteBusy ? 'Deleting…' : 'Permanently delete my account'}
              </SolidButton>
            </div>
          </Notice>
        )}
      </Block>
    </div>
  )
}
